import React, { useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useCombobox, useMultipleSelection } from 'downshift';
import styled from 'styled-components';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';
import { ShareModalSelectTextField } from '../ShareModalSelectTextField';

import { DropdownMenuItem, Avatar, LetterAvatar, Text, Icon } from '../../../../../components';

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  position: relative;
  width: 100%;
  padding-bottom: 12px;
  z-index: 1;
`;

const StyledDropdownMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 2px;
  position: absolute;
  z-index: var(--z-dropdown);
  top: 45px;
  max-height: 400px;
  box-sizing: border-box;
  overflow-y: scroll;
  width: 100%;
  max-width: 536px;
  border-radius: 8px;
  border: 1px solid var(--border-muted);
  background-color: var(--elevation-high);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.04), 0px 4px 20px 0px rgba(0, 0, 0, 0.05);
`;

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
  display: flex;
  align-items: center;
  flex-direction: row;
  column-gap: 8px;
  height: 40px;
  box-sizing: border-box;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  justify-content: space-between;
  align-items: center;
`;

const SelectedItemsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--border-light);
  min-height: 40px;
  box-sizing: border-box;
  height: 100%;
  padding: 6px;
`;

const StyledText = styled(Text)`
  display: flex;
  padding: 3px 8px;
  align-items: center;
  border-radius: 4px;
  background-color: var(--gray-4);
  color: var(--black);
`;

const NoPeopleMatchingLabel = styled(Text)`
  padding: 14px 8px;
`;

const CloseIcon = styled(Icon)`
  cursor: pointer;
`;

const getFilteredUsers = (users, selectedItems, inputValue = '') =>
  users
    .filter((user) => !selectedItems.includes(user))
    .filter((user) => user.name?.toLowerCase().includes(inputValue.toLowerCase()))
    .filter(Boolean);

const ShareModalSelect = ({ users, setSelectedItems, label, selectedItems = [] }) => {
  const [inputValue, setInputValue] = useState('');

  const items = useMemo(
    () => getFilteredUsers(users, selectedItems, inputValue),
    [users, selectedItems, inputValue]
  );

  const inputRef = useRef();

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate
  });

  const setMenuRef = useCallback(
    (node) => {
      // Update floating UI ref
      refs.setFloating(node);
    },
    [refs.setFloating]
  );

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } = useMultipleSelection({
    selectedItems,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          setSelectedItems(newSelectedItems);
          break;
        default:
          break;
      }
    }
  });

  const { isOpen, getMenuProps, getInputProps, getItemProps, openMenu } = useCombobox({
    items,
    itemToString(item) {
      return item ? item?.name : '';
    },
    selectedItem: null,
    inputValue,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: 0
          };
        default:
          return changes;
      }
    },
    onStateChange(a) {
      const { inputValue: newInputValue, type, selectedItem: newSelectedItem } = a;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (newSelectedItem) {
            setSelectedItems([...selectedItems, newSelectedItem]);
            inputRef.current.value = '';
            setInputValue('');
            openMenu();
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue);
          break;
        default:
          break;
      }
    }
  });

  // Get menu props once and reuse
  const menuProps = getMenuProps({
    ref: setMenuRef
  });

  const renderSelectedItem = (selectedItemForRender, index) => (
    <StyledText
      key={`selected-item-${index}`}
      {...getSelectedItemProps({
        selectedItem: selectedItemForRender,
        index
      })}
    >
      {selectedItemForRender.name}
      <CloseIcon
        name='close'
        color='var(--content-body)'
        displaySize={20}
        onClick={(e) => {
          e.stopPropagation();
          removeSelectedItem(selectedItemForRender);
        }}
      />
    </StyledText>
  );

  return (
    <SelectContainer>
      <FormWrapper>
        <SelectedItemsWrapper
          ref={refs.setReference}
          autoComplete='off'
          role='combobox'
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          data-form-type='other'
          data-lpignore='true'
        >
          {selectedItems.map(renderSelectedItem)}
          <ShareModalSelectTextField
            name='share-modal-select'
            {...getInputProps(
              getDropdownProps({
                placeholder: selectedItems.length < 1 ? label : '',
                type: 'search',
                autoComplete: 'chrome-off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: 'false',
                inputMode: 'none',
                results: '0',
                maxLength: '524288',
                ref: inputRef,
                preventKeyAction: isOpen,
                'data-lpignore': 'true',
                'data-form-type': 'other',
                onFocus: () => {
                  openMenu();
                }
              })
            )}
          />
        </SelectedItemsWrapper>
      </FormWrapper>
      {isOpen && (
        <StyledDropdownMenu style={floatingStyles} {...menuProps} role='listbox'>
          {items.length === 0 && (
            <NoPeopleMatchingLabel $color='var(--content-muted)'>
              {users.length === selectedItems.length
                ? 'Looks like you selected all available people'
                : 'No people matching the name you provided'}
            </NoPeopleMatchingLabel>
          )}
          {items.map((item, index) => (
            <StyledDropdownMenuItem
              key={item?.id}
              {...getItemProps({
                item,
                index,
                leftIcon: item.picture ? (
                  <Avatar width={24} height={24} src={item?.picture} />
                ) : (
                  <LetterAvatar size={24} name={item?.email} />
                )
              })}
            >
              {item.name}
            </StyledDropdownMenuItem>
          ))}
        </StyledDropdownMenu>
      )}
    </SelectContainer>
  );
};

const user = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  picture: PropTypes.string
});

ShareModalSelect.propTypes = {
  users: PropTypes.arrayOf(user).isRequired,
  selectedItems: PropTypes.arrayOf(user),
  setSelectedItems: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired
};

export { ShareModalSelect };
